import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, firstValueFrom, map } from 'rxjs';
import { PlanillaDTO } from './planillas.models';
import {
  ListaDTO,
  Parametros,
} from '../../compartido/utilidades/compartido.models';
import { ErrorFrontend } from 'src/app/compartido/utilidades/error.utils';
import { Planilla } from './planilla.class';

@Injectable({ providedIn: 'root', deps: [HttpClient] })
export class PlanillasService {
  constructor(private readonly _http: HttpClient) {}

  /**
   * Obtiene las planillas del servidor.
   * @param {Parametros} params - Los parámetros de búsqueda.
   * @returns {Promise<ListaDTO<PlanillaDTO>>} - Los planillas obtenidas del servidor (promise).
   * @throws {ErrorFrontend} - Si hubo un error al obtener las planillas.
   * @author Juan Corral
   */
  public obtenerPlanillas$(params: Parametros): Promise<ListaDTO<PlanillaDTO>> {
    return firstValueFrom(
      this._http.get<ListaDTO<PlanillaDTO>>('planillas/', { params }).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /**
   * Obtiene información de la planilla del servidor.
   * @param {number} id - El id de la planilla a obtener.
   * @returns {Promise<Planilla> } - La respuesta del servidor (promise).
   * @throws {ErrorFrontend} - Si hubo un error al obtener la planilla.
   * @author Juan Corral
   */
  public obtenerPlanilla$(id: number): Promise<Planilla> {
    return firstValueFrom(
      this._http.get<PlanillaDTO>('planillas/' + id).pipe(
        map((planilla: PlanillaDTO) => Planilla.fromDTO(planilla)),
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /**
   * Descarga un archivo Excel con las órdenes de la planilla.
   * @param {number} id - El id de la planilla a descargar.
   * @throws {ErrorFrontend} - Si hubo un error al descargar la planilla.
   * @author Juan Corral
   */
  public async descargarPlanilla$(id: number, tipo: string): Promise<void> {
    const params: Parametros = { tipo };
    await firstValueFrom(
      this._http
        .get(`planillas/${id}/descargar/`, { params, responseType: 'blob' })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            throw new ErrorFrontend(error);
          }),
          map((blob: Blob) => {
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `planilla_${tipo}.xlsx`;
            link.click();
            URL.revokeObjectURL(link.href);
            return;
          }),
        ),
    );
  }

  /**
   * Envía un archivo ZIP con las fotos de checkout de la planilla al correo del usuario.
   * @param {number} id - El id de la planilla a enviar.
   * @throws {ErrorFrontend} - Si hubo un error al enviar las fotos de checkout.
   * @author Juan Corral
   */
  public async enviarFotosCheckout$(id: number): Promise<void> {
    await firstValueFrom(
      this._http.get(`planillas/${id}/enviar_fotos_checkout/`).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }
}
